// src/components/guards/SubscriptionGuard.jsx  ·  SPRINT FIX v2.2
//
// CAMBIOS vs v2.1:
//   ✅ `overdue` ya no pinta un recuadro aquí: manda a /reactivar (17-09-2026)
//   ✅ Hook useSubscriptionStatus ahora expone plan, expiryDate, daysOverdue, tokensLeft, tokensMax
//   ✅ Caché ampliada para guardar todo el objeto de suscripción
//   ✅ clearSubscriptionCache compatible con la nueva estructura

import { useState, useEffect, useRef, Component } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import "./SubscriptionGuard.css";
import { useLatido } from "../../hooks/useLatido";

// ---------- CLAVES DE CACHÉ ----------
const CACHE_KEY_SUB_DATA = "nsl_sub_data";   // objeto completo { status, plan, expiryDate, daysOverdue, ... }
const CACHE_KEY_TIME      = "nsl_sub_data_at";
const CACHE_MS            = 15_000;

export function clearSubscriptionCache() {
  sessionStorage.removeItem(CACHE_KEY_SUB_DATA);
  sessionStorage.removeItem(CACHE_KEY_TIME);
}

// ---------- CONFIGURACIÓN PARA PAYWALL (paused / tokens) ----------
const BLOCK_CONFIG = {
  overdue: {   // ya no se usa: `overdue` sale de aquí hacia /reactivar. Se deja por compatibilidad.
    icon:       "🔒",
    title:      "Pago Vencido",
    subtitle:   "Tu suscripción expiró.",
    body:       "Renueva hoy y recupera acceso instantáneo a tus clases, tutores y juegos de inglés.",
    badge:      "PAGO REQUERIDO",
    badgeColor: "var(--sg-er)",
    cta:        "💳 Renovar Ahora",
    ctaStyle:   "primary",
    hint:       "Tarda menos de 2 minutos · Sin contratos",
  },
  paused: {
    icon:       "⏸️",
    title:      "Acceso Pausado",
    subtitle:   "Tu cuenta está suspendida.",
    body:       "Contacta a tu tutor o al equipo NSL para reactivar tu acceso inmediatamente.",
    badge:      "CUENTA SUSPENDIDA",
    badgeColor: "var(--sg-tx2)",
    cta:        "📱 Contactar a NSL",
    ctaStyle:   "secondary",
    hint:       "Respuesta en menos de 1 hora",
  },
  tokens: {
    icon:       "⚠️",
    title:      "Tokens IA Agotados",
    subtitle:   "Has usado todos tus créditos de IA este mes.",
    body:       "Compra más tokens o sube de plan para seguir con Spacy. El resto de tu entrenamiento —lecciones, glosario y minijuegos— sigue abierto.",
    badge:      "SIN CRÉDITOS IA",
    badgeColor: "var(--sg-wrn)",
    cta:        "⚡ Comprar más tokens",
    ctaStyle:   "primary",
    hint:       "Los tokens se renuevan el 1° de cada mes",
  },
};

// ---------- HOOK PRINCIPAL (ahora con más campos) ----------
export function useSubscriptionStatus() {
  const [status,      setStatus]      = useState(null);
  const [plan,        setPlan]        = useState(null);
  const [expiryDate,  setExpiryDate]  = useState(null);
  const [daysOverdue, setDaysOverdue] = useState(null);
  const [pruebaVencida, setPruebaVencida] = useState(false);
  const [tokensLeft,  setTokensLeft]  = useState(null);
  const [tokensMax,   setTokensMax]   = useState(null);
  const [xp,          setXp]          = useState(0);
  // Un profesor no tiene vencimiento: tiene un día de RENOVACIÓN, contado desde
  // su inscripción. Sin estos tres, su menú pintaba «Renovación ∞».
  const [esEquipo,    setEsEquipo]    = useState(false);
  const [renueva,     setRenueva]     = useState(null);
  const [inscripcion, setInscripcion] = useState(null);
  // Sin suscripción ninguna: no es «prueba vencida» ni «pago vencido».
  const [sinPlan,     setSinPlan]     = useState(false);
  // Empleado de empresa: nombre, vencimiento, gracia, pausa o baja. Lo decide
  // el backend (`estado_por_contrato`); aquí solo se lee para elegir el mensaje.
  const [empresa,     setEmpresa]     = useState(null);
  const [loading,     setLoading]     = useState(true);
  // La huella del último estado conocido: el latido la usa para saber si
  // esta vuelta trajo algo nuevo o puede aflojar el ritmo.
  const estadoRef = useRef("");

  async function fetchStatus(bypassCache = false) {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setStatus("paused");
        setLoading(false);
        return;
      }

      // ---- CACHÉ ----
      if (!bypassCache) {
        const cachedData = sessionStorage.getItem(CACHE_KEY_SUB_DATA);
        const cachedAt   = sessionStorage.getItem(CACHE_KEY_TIME);
        if (cachedData && cachedAt && Date.now() - Number(cachedAt) < CACHE_MS) {
          const data = JSON.parse(cachedData);
          setStatus(data.status);
          setPlan(data.plan);
          setExpiryDate(data.expiryDate);
          setDaysOverdue(data.daysOverdue);
          // Faltaba, y costaba caro: cada pantalla que llama a este hook tiene
          // su propia copia del estado, y la que llegaba con la caché caliente
          // se quedaba con `pruebaVencida = false`. La pared de reactivación le
          // decía «tu plan terminó» a quien solo había probado, y la barrera de
          // `ProtectedRoute` dejaba pasar al panel a quien no debía.
          setPruebaVencida(data.pruebaVencida === true);
          setTokensLeft(data.tokensLeft);
          setTokensMax(data.tokensMax);
          setXp(data.xp || 0);
          setEsEquipo(data.esEquipo === true);
          setRenueva(data.renueva || null);
          setInscripcion(data.inscripcion || null);
          setSinPlan(data.sinPlan === true);
          setEmpresa(data.empresa || null);
          setLoading(false);
          return;
        }
      }

      // ---- PETICIÓN REAL ----
      const res = await fetch("/api/subscription/status", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        if (res.status === 402 || res.status === 403 || res.status === 429) {
          try {
            const errBody = await res.json();
            const errStatus = errBody?.detail?.subscription_status || errBody?.subscription_status || "paused";
            // Fallback: sin datos extra
            setStatus(errStatus);
            setPlan(null);
            setExpiryDate(null);
            setDaysOverdue(null);
            // Sin datos no se arrastra el POR QUÉ de la vez anterior: la pared
            // de reactivación elige el mensaje con estas dos, y un `true` viejo
            // le haría decir «se acabó tu prueba» a quien no tuvo ninguna.
            setPruebaVencida(false);
            setSinPlan(false);
            setTokensLeft(null);
            setTokensMax(null);
            setEsEquipo(false);
            setRenueva(null);
            setInscripcion(null);
            setEmpresa(null);
            // Guardar en caché
            const cacheObj = {
              status: errStatus,
              plan: null,
              expiryDate: null,
              daysOverdue: null,
              pruebaVencida: false,
              sinPlan: false,
              tokensLeft: null,
              tokensMax: null,
              xp: 0,
            };
            sessionStorage.setItem(CACHE_KEY_SUB_DATA, JSON.stringify(cacheObj));
            sessionStorage.setItem(CACHE_KEY_TIME, String(Date.now()));
          } catch {
            setStatus("paused");
          }
        } else {
          setStatus(prev => prev === null ? "active" : prev);
        }
        setLoading(false);
        return;
      }

      const data = await res.json();
      // Mapeo de campos según la respuesta de tu backend
      const subStatus = data.subscription_status || data.status || "active";
      const planName  = data.plan_name || data.plan || null;
      const expDate   = data.expiry_date || data.expiryDate || null;
      const overdue   = data.days_overdue ?? data.daysOverdue ?? null;
      // Lo decide el backend, no esta pantalla: la regla de que es una prueba
      // vencida vive en `prueba_gratis.py` y aqui solo se lee.
      const esPrueba  = data.prueba_vencida === true;
      const tLeft     = data.tokens_remaining ?? data.tokens_left ?? data.tokensLeft ?? null;
      const tMax      = data.token_limit ?? data.tokens_max ?? data.tokensMax ?? null;
      const userXp    = data.xp ?? 0;
      const equipo    = data.es_equipo === true;
      const renuevaEl = data.renueva || null;
      const inscrito  = data.inscripcion || null;
      const sin       = data.sin_plan === true;
      const deEmpresa = data.empresa || null;

      console.log("🛡️ GUARD STATUS:", subStatus, "| PLAN:", planName);

      setStatus(subStatus);
      setPlan(planName);
      setExpiryDate(expDate);
      setDaysOverdue(overdue);
      setPruebaVencida(esPrueba);
      setTokensLeft(tLeft);
      setTokensMax(tMax);
      setXp(userXp);
      setEsEquipo(equipo);
      setRenueva(renuevaEl);
      setInscripcion(inscrito);
      setSinPlan(sin);
      setEmpresa(deEmpresa);

      // Guardar en caché
      const cacheObj = {
        status: subStatus,
        plan: planName,
        expiryDate: expDate,
        daysOverdue: overdue,
        pruebaVencida: esPrueba,
        tokensLeft: tLeft,
        tokensMax: tMax,
        xp: userXp,
        esEquipo: equipo,
        renueva: renuevaEl,
        inscripcion: inscrito,
        sinPlan: sin,
        empresa: deEmpresa,
      };
      sessionStorage.setItem(CACHE_KEY_SUB_DATA, JSON.stringify(cacheObj));
      sessionStorage.setItem(CACHE_KEY_TIME, String(Date.now()));
      // Lo que se compara entre una vuelta y la siguiente: si no cambió nada,
      // el latido afloja el ritmo (ver más abajo).
      estadoRef.current = JSON.stringify(cacheObj);

    } catch (error) {
      console.error("🛡️ Falla de red en Guardián:", error);
      setStatus(prev => prev === null ? "active" : prev);
    } finally {
      setLoading(false);
    }
  }

  // ── EL GUARDIÁN PREGUNTABA CADA 8 SEGUNDOS, SIEMPRE (19-09-2026) ────────
  // Este hook vive en las pantallas protegidas, así que eran 450 consultas por
  // hora POR ALUMNO conectado, mirase o no la pantalla. Es el goteo más caro
  // del lado del alumno, y el que más crece con el negocio: cada alumno nuevo
  // suma sus 450.
  //
  // Lo que el guardián tiene que detectar —que gerencia suspenda un plan, que
  // venza una prueba— no pasa de un segundo para otro, y lo importante ya está
  // cubierto: se vuelve a preguntar al recuperar el foco de la ventana. Así que
  // el ritmo arranca igual de vivo y va aflojando si nada cambia, y con la
  // pestaña oculta no se pregunta nada.
  useLatido(async () => {
    const antes = estadoRef.current;
    await fetchStatus(true);
    // Si algo cambió de verdad, el latido vuelve al ritmo rápido.
    return estadoRef.current !== antes;
  }, { base: 8_000, techo: 120_000 });

  useEffect(() => {
    fetchStatus();
    const handleFocus = () => fetchStatus(true);
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);

  return { status, plan, expiryDate, daysOverdue, pruebaVencida, sinPlan, empresa, tokensLeft, tokensMax, xp, esEquipo, renueva, inscripcion, loading };
}

// ---------- NUEVO BANNER PARA OVERDUE ----------
// ---------- EL RECUADRO ROJO DE «PAGO VENCIDO»: ELIMINADO (17-09-2026) ----
//
// Aquí vivía `OverdueBanner`, un modal oscuro con borde rojo que se pintaba
// encima de la pantalla cuando el estado era `overdue`. Se eliminó por dos
// razones, y la segunda es la de fondo:
//
//   1. PARPADEABA. Este componente y `ProtectedRoute` leen el estado cada uno
//      por su cuenta, y no terminan de cargar a la vez. El alumno pulsaba una
//      pestaña del menú, veía el recuadro rojo un par de segundos y después la
//      pared de reactivación. Dos avisos para un solo hecho, y el peor primero.
//
//   2. NO ERA NSL. Fondo negro, verde neón, borde rojo y «Pago vencido» como
//      titular: el tono de un cobro, justo en el momento de convertir. Rob,
//      17-09-2026: «vamos a dejar el que me gusta para cuando alguien concluye
//      su plan freemium o vence su plan de pago».
//
// Ahora `overdue` manda a `/reactivar` —la pared con la estética de NSL—, que
// elige el mensaje según por qué se quedó fuera. Lo que sigue aquí es el muro
// de `paused` y `tokens`, que son otra cosa: ésos no echan a nadie de la app.

// ---------- ERROR BOUNDARY Y COMPONENTES AUXILIARES (sin cambios) ----------
class BlurLayerErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

function Particles({ color }) {
  const count = 18;
  return (
    <div className="sg-particles" aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <span
          key={i}
          className="sg-particle"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            width: `${2 + Math.random() * 3}px`,
            height: `${2 + Math.random() * 3}px`,
            background: color,
            animationDelay: `${Math.random() * 4}s`,
            animationDuration: `${3 + Math.random() * 4}s`,
          }}
        />
      ))}
    </div>
  );
}

function LockIcon({ color }) {
  return (
    <svg className="sg-lock-svg" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="sg-glow">
          <feGaussianBlur stdDeviation="3" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <rect x="12" y="28" width="40" height="28" rx="6"
        fill={color} fillOpacity=".15" stroke={color} strokeWidth="2" filter="url(#sg-glow)" />
      <path d="M20 28V20a12 12 0 0 1 24 0v8"
        stroke={color} strokeWidth="2.5" strokeLinecap="round" fill="none" filter="url(#sg-glow)" />
      <circle cx="32" cy="42" r="4" fill={color} filter="url(#sg-glow)" />
      <rect x="30" y="42" width="4" height="6" rx="1" fill={color} />
    </svg>
  );
}

// ── EL PROGRAMA DE EMPRESA EN PAUSA (EMPRESAS.md, D13) ──────────────────────
// La empresa está pausada, o pasó su gracia de 7 días sin renovar. El texto es el
// que pidió Rob, «un mensaje sutil»: al empleado no se le culpa ni se le cobra
// nada —el contrato es de su empresa— y se le deja un camino para preguntar.
const PROGRAMA_EN_PAUSA = {
  icon:       "⏸️",
  title:      "Programa en pausa",
  subtitle:   "",
  body:       "El programa está en pausa hasta nuevo aviso. Te estaremos comunicando vía email o por nuestro chat general de Non Stop Learning. Para mayor información puedes enviarnos un WhatsApp.",
  badge:      "PROGRAMA EN PAUSA",
  badgeColor: "var(--sg-tx2)",
  cta:        "📱 Escribir por WhatsApp",
  ctaStyle:   "secondary",
  hint:       "Tu progreso, tu nivel y tus rachas siguen guardados",
};

export function PaywallOverlay({ blockStatus, isEmbedded = false, empresa = null }) {
  const navigate = useNavigate();
  const programaEnPausa = blockStatus === "paused" && (empresa?.pausada || empresa?.vencida);
  const cfg = programaEnPausa
    ? { ...PROGRAMA_EN_PAUSA,
        subtitle: empresa?.nombre ? `Tu programa de entrenamiento con ${empresa.nombre}.` : "" }
    : (BLOCK_CONFIG[blockStatus] || BLOCK_CONFIG.paused); // fallback a paused si no existe (overdue ya no entra aquí)
  const accentColor = cfg.badgeColor;
  const cardRef = useRef(null);

  useEffect(() => {
    const card = cardRef.current;
    if (card) {
      card.style.opacity = "0";
      card.style.transform = "translateY(32px) scale(0.95)";
      requestAnimationFrame(() => {
        card.style.transition = "opacity .45s cubic-bezier(.22,1,.36,1), transform .45s cubic-bezier(.22,1,.36,1)";
        card.style.opacity = "1";
        card.style.transform = "translateY(0) scale(1)";
      });
    }
  }, []);

  function handleCTA() {
    if (programaEnPausa) {
      // Sin emojis a propósito: WhatsApp los pinta como rombos rotos.
      const texto = `Hola, soy parte del programa de ${empresa?.nombre || "mi empresa"} en NSL y mi acceso aparece en pausa. ¿Me pueden informar?`;
      window.open(`https://wa.me/5215610480746?text=${encodeURIComponent(texto)}`, "_blank");
      return;
    }
    if (blockStatus === "paused") {
      window.open("https://wa.me/5215610480746?text=Hola%2C%20quiero%20reactivar%20mi%20cuenta%20NSL", "_blank");
    } else {
      navigate("/plans?tab=tokens");
    }
  }

  return (
    <div 
      className="sg-overlay" 
      role="dialog" 
      aria-modal="true" 
      aria-label="Acceso restringido"
      style={isEmbedded ? { position: 'absolute', zIndex: 10, borderRadius: 'inherit' } : undefined}
    >
      <Particles color={accentColor} />
      <div className="sg-noise" aria-hidden="true" />

      <div ref={cardRef} className="sg-card" style={{ "--sg-accent": accentColor }}>
        <div className="sg-card-line" style={{ background: accentColor }} />

        <div className="sg-badge" style={{
          color: accentColor,
          borderColor: `${accentColor}44`,
          background: `${accentColor}12`,
        }}>
          <span className="sg-badge-dot" style={{ background: accentColor, boxShadow: `0 0 6px ${accentColor}` }} />
          {cfg.badge}
        </div>

        <div className="sg-icon-wrap">
          <LockIcon color={accentColor} />
          <div className="sg-icon-glow" style={{ background: accentColor }} />
        </div>

        <h2 className="sg-title">{cfg.title}</h2>
        <p className="sg-subtitle">{cfg.subtitle}</p>
        <p className="sg-body">{cfg.body}</p>

        <div className="sg-divider" />

        <button
          className={`sg-btn sg-btn-${cfg.ctaStyle}`}
          onClick={handleCTA}
          style={cfg.ctaStyle === "primary" ? {
            background: `${accentColor}18`,
            color: accentColor,
            borderColor: `${accentColor}50`,
            boxShadow: `0 0 0 1px ${accentColor}25`,
          } : undefined}
        >
          {cfg.cta}
        </button>

        <p className="sg-hint">{cfg.hint}</p>

        {/* ── LA SALIDA, Y POR QUÉ NO SIEMPRE ES «VOLVER» ──────────────────
            Desde el 27-09-2026 el Coach es la pantalla que RECIBE al alumno
            al iniciar sesión. Quien se haya quedado sin Energía Spacy aterriza
            aquí, y entonces «← Volver» no lleva a ninguna parte: detrás sólo
            está el login.

            Y quedarse sin Energía no cierra la app, sólo lo que gasta IA: el
            gimnasio sigue abierto entero. Así que cuando el muro es por
            Energía, la salida es el gimnasio — lo que el alumno SÍ puede
            hacer ahora mismo— en vez de una puerta a ninguna parte. Con el
            plan vencido o en pausa se mantiene «volver», porque ahí el
            gimnasio también está cerrado y mandarle allí sería un rebote. */}
        {blockStatus === "tokens" ? (
          <button className="sg-back-btn" onClick={() => navigate("/entrenar")}>
            ← Seguir entrenando sin IA
          </button>
        ) : (
          <button className="sg-back-btn" onClick={() => navigate(-1)}>
            ← Volver
          </button>
        )}
      </div>
    </div>
  );
}

// ---------- COMPONENTE PRINCIPAL SubscriptionGuard ----------
export default function SubscriptionGuard({ children, blockOnTokens = true }) {
  const { status, empresa, loading } = useSubscriptionStatus();

  if (loading) return <>{children}</>;
  if (status === "active") return <>{children}</>;

  // Caso especial: tokens y blockOnTokens=false → dejar pasar
  if (status === "tokens" && !blockOnTokens) return <>{children}</>;

  // `overdue` → la pared de reactivación, la misma para los cuatro finales.
  // Aquí no se pinta nada encima de la pantalla: se cambia de pantalla. Un
  // aviso flotante deja ver por debajo lo que ya no se puede usar, y encima
  // este componente y `ProtectedRoute` no cargan a la vez, así que se veían
  // los dos avisos seguidos. `replace` para que el botón de atrás no devuelva
  // a la pantalla de la que se le acaba de apartar.
  if (status === "overdue") {
    return <Navigate to="/reactivar" replace />;
  }

  // Para "paused" o "tokens" (si no pasó por el filtro blockOnTokens) usamos el PaywallOverlay original
  const blockStatus = ["paused", "tokens"].includes(status) ? status : "paused";
  return (
    <div className="sg-root">
      <BlurLayerErrorBoundary>
        <div className="sg-blur-layer" aria-hidden="true" inert="">
          {children}
        </div>
      </BlurLayerErrorBoundary>

      <PaywallOverlay blockStatus={blockStatus} empresa={empresa} />
    </div>
  );
}